import * as React from 'react'
import { useState } from 'react'
import {
  Box,
  Typography,
  Chip,
  Tooltip,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material'
import {
  List,
  DatagridConfigurable,
  FunctionField,
  TopToolbar,
  SelectColumnsButton,
  TextInput,
  SelectInput,
  useGetList,
  ReferenceField,
  TextField,
  ExportButton,
  downloadCSV,
  useRefresh,
  DateInput,
  useNotify,
  useStore,
  Pagination,
  useListContext,
  useListFilterContext,
} from 'react-admin'
import { fromTimestamp, formatDateToMMDDYYYY, formatPhoneDisplay } from '../../utils/helpers'
import { client } from '../../providers/data/client'

// Status chip styling
const REQUEST_STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'warning' },
  convertedToOrder: { label: 'Order Created', color: 'success' },
  failedToCreate: { label: 'Failed to Create', color: 'error' },
  canceled: { label: 'Canceled', color: 'error' },
}

const FINANCIAL_STATUS_CONFIG = {
  none: { label: 'None', color: 'default' },
  invoiced: { label: 'Invoiced', color: 'warning' },
  paid: { label: 'Paid', color: 'success' },
}

const StatusChip = ({ value, config }) => {
  const cfg = config[value] || { label: value || '—', color: 'default' }
  return <Chip label={cfg.badgeLabel || cfg.label} color={cfg.color} size="small" sx={{ fontWeight: 500, minWidth: 90 }} />
}

const ProviderSelectFilter = (props) => {
  const { data, isLoading } = useGetList('provider-portal/providers', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'displayName', order: 'ASC' },
  })

  return <SelectInput emptyText={'All Providers'} {...props} choices={data || []} optionText="displayName" isLoading={isLoading} />
}

const ProviderGroupSelectFilter = (props) => {
  const { data, isLoading } = useGetList('provider-portal/provider-groups', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'name', order: 'ASC' },
  })

  return <SelectInput emptyText={'All Groups'} {...props} choices={data || []} optionText="name" isLoading={isLoading} />
}

const filters = [
  <TextInput label="Request ID" source="requestId" alwaysOn sx={{ width: '250px' }} resettable />,
  <ProviderGroupSelectFilter alwaysOn resettable label="Provider Group" source="providerGroupId" sx={{ width: '250px' }} />,
  <ProviderSelectFilter label="Provider Name" source="providerId" alwaysOn sx={{ width: '250px' }} resettable />,
  <DateInput alwaysOn label="Date Created" source="createdDate" sx={{ width: '210px' }} resettable />,
  <SelectInput
    label="Order Status"
    source="requestStatus"
    emptyText={'All Statuses'}
    choices={[
      { id: 'pending', name: 'Pending' },
      { id: 'convertedToOrder', name: 'Order Created' },
      { id: 'failedToCreate', name: 'Failed to Create' },
      { id: 'canceled', name: 'Canceled' },
    ]}
    alwaysOn
    sx={{ width: '250px' }}
    resettable
  />,
  <TextInput label="Patient Name" source="patientName" alwaysOn sx={{ width: '250px' }} resettable />,
  <DateInput alwaysOn label="Date of Birth" source="patientDob" sx={{ width: '210px' }} resettable />,
  <TextInput label="Patient Email" source="email" alwaysOn sx={{ width: '250px' }} resettable />,
  <TextInput label="Shipped Invoice ID" source="shippedInvoiceId" alwaysOn sx={{ width: '250px' }} resettable />,
  <TextInput label="Tested Invoice ID" source="testedInvoiceId" alwaysOn sx={{ width: '250px' }} resettable />,
]

// Confirmation dialog for Cancel / Restore actions
const ConfirmDialog = ({ open, title, message, onConfirm, onCancel, loading }) => (
  <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
    <DialogTitle>{title}</DialogTitle>
    <DialogContent>
      <DialogContentText>{message}</DialogContentText>
    </DialogContent>
    <DialogActions>
      <Button onClick={onCancel} disabled={loading}>
        No, keep it
      </Button>
      <Button
        onClick={onConfirm}
        color="error"
        variant="contained"
        disabled={loading}
        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
      >
        {loading ? 'Processing…' : 'Yes, confirm'}
      </Button>
    </DialogActions>
  </Dialog>
)

// Row-level action buttons
const RowActions = ({ record, onEdit, refresh }) => {
  const [dialog, setDialog] = React.useState(null) // { type: 'cancel' }
  const [loading, setLoading] = React.useState(false)

  const handleAction = async (type) => {
    setLoading(true)
    try {
      await client().post(`/provider-portal/kit-requests/${record.id}/${type}`)
      setDialog(null)
      refresh()
    } catch (err) {
      console.error(`Error during ${type}:`, err)
      alert(err.response?.data?.message || `Failed to ${type} the kit request.`)
    } finally {
      setLoading(false)
    }
  }

  const { requestStatus } = record

  if (requestStatus !== 'pending') {
    return null
  }

  return (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'nowrap' }}>
      <Button
        size="small"
        variant="contained"
        onClick={() => onEdit(record)}
        sx={{ whiteSpace: 'nowrap', textTransform: 'none', fontSize: '12px' }}
      >
        Edit
      </Button>
      <Button
        size="small"
        variant="outlined"
        color="error"
        onClick={() => setDialog({ type: 'cancel' })}
        sx={{ whiteSpace: 'nowrap', textTransform: 'none', fontSize: '12px' }}
      >
        Cancel
      </Button>

      <ConfirmDialog
        open={dialog?.type === 'cancel'}
        title="Cancel Kit Request"
        message="Are you sure you want to cancel this kit request? This action cannot be undone."
        loading={loading}
        onConfirm={() => handleAction('cancel')}
        onCancel={() => setDialog(null)}
      />
    </Box>
  )
}

const ClearFiltersButton = () => {
  const { filterValues, setFilters } = useListFilterContext()
  const hasFilters = Object.keys(filterValues).length > 0
  if (!hasFilters) return null
  return (
    <Button size="small" variant="outlined" onClick={() => setFilters({}, [])} sx={{ textTransform: 'none', fontSize: '13px' }}>
      Clear Filters
    </Button>
  )
}

const TopPagination = () => {
  const { page, setPage, total, perPage } = useListContext()
  if (!total || total <= perPage) return null
  const totalPages = Math.ceil(total / perPage)
  return (
    <Box display="flex" alignItems="center" gap={0.5}>
      <Button size="small" variant="text" disabled={page <= 1} onClick={() => setPage(page - 1)} sx={{ minWidth: 0, p: '4px' }}>‹</Button>
      <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>{page} / {totalPages}</Typography>
      <Button size="small" variant="text" disabled={page >= totalPages} onClick={() => setPage(page + 1)} sx={{ minWidth: 0, p: '4px' }}>›</Button>
    </Box>
  )
}

const ListActions = ({ exporter }) => (
  <TopToolbar>
    <ClearFiltersButton />
    <SelectColumnsButton />
    <ExportButton exporter={exporter} label="Download CSV" />
    <TopPagination />
  </TopToolbar>
)

const KitRequestList = ({ onEdit }) => {
  const refresh = useRefresh()
  const notify = useNotify()

  const resource = 'provider-portal/kit-requests'
  const preferenceKey = `${resource}.datagrid`
  const [availableColumns] = useStore(`preferences.${preferenceKey}.availableColumns`, [])
  const [omit] = useStore(`preferences.${preferenceKey}.omit`, [])
  const [columns] = useStore(`preferences.${preferenceKey}.columns`)

  const kitRequestExporter = React.useCallback(
    (records, fetchRelatedRecords) => {
      if (!records || records.length === 0) return

      // Get visible sources from store preferences
      const visibleSources =
        columns !== undefined
          ? columns.map((idx) => availableColumns.find((col) => col.index === idx)?.source).filter(Boolean)
          : availableColumns
              .filter((column) => !omit?.includes(column.source))
              .map((column) => column.source)
              .filter(Boolean)

      // Map source keys to CSV header names
      const sourceToHeaderMap = {
        requestId: ['Request ID'],
        providerGroupId: ['Provider Group'],
        clinicName: ['Clinic Name'],
        providerName: ['Provider Name'],
        createdAt: ['Date Created'],
        requestStatus: ['Status', 'Financial Status'],
        patientName: ['Patient First Name', 'Patient Last Name'],
        patientDob: ['Date of Birth'],
        dateOfVasectomy: ['Date of Vasectomy'],
        shippedDate: ['Ship Date'],
        targetConvertDate: ['Target Convert Date'],
        phoneNumber: ['Patient Phone', 'Patient Email'],
        shippedInvoiceId: ['Shipped Invoice ID'],
        testedInvoiceId: ['Tested Invoice ID'],
      }

      // Default headers to export
      let headersToExport = [
        'Request ID',
        'Provider Group',
        'Clinic Name',
        'Provider Name',
        'Date Created',
        'Status',
        'Financial Status',
        'Patient First Name',
        'Patient Last Name',
        'Date of Birth',
        'Date of Vasectomy',
        'Ship Date',
        'Target Convert Date',
        'Patient Phone',
        'Patient Email',
      ]

      // If availableColumns are present in the preferences store, filter headers accordingly
      if (availableColumns && availableColumns.length > 0) {
        headersToExport = headersToExport.filter((header) => {
          return Object.entries(sourceToHeaderMap).some(([source, headers]) => {
            return visibleSources.includes(source) && headers.includes(header)
          })
        })
      }

      if (headersToExport.length === 0) {
        notify('No visible columns to export', { type: 'warning' })
        return
      }

      fetchRelatedRecords(records, 'providerGroupId', 'provider-portal/provider-groups').then((providerGroups) => {
        const rows = records.map((r) => {
          const row = {
            'Request ID': r.requestId || '',
            'Provider Group': providerGroups[r.providerGroupId]?.name || r.providerGroupId || '',
            'Clinic Name': r.clinicName || '',
            'Provider Name': r.providerName || '',
            'Date Created': r.createdAt ? new Date(r.createdAt).toLocaleDateString() : '',
            Status: REQUEST_STATUS_CONFIG[r.requestStatus]?.label || r.requestStatus || '',
            'Financial Status': FINANCIAL_STATUS_CONFIG[r.shippedFinancialStatus]?.label || r.shippedFinancialStatus || '',
            'Patient First Name': r.patientFirstName || '',
            'Patient Last Name': r.patientLastName || '',
            'Date of Birth': formatDateToMMDDYYYY(r.patientDob),
            'Date of Vasectomy': formatDateToMMDDYYYY(r.dateOfVasectomy),
            'Ship Date': formatDateToMMDDYYYY(r.shippedDate),
            'Target Convert Date': formatDateToMMDDYYYY(r.targetConvertDate),
            'Patient Phone': r.phoneNumber || '',
            'Patient Email': r.email || '',
          }

          // Keep only visible columns in the row
          const filteredRow = {}
          headersToExport.forEach((h) => {
            filteredRow[h] = row[h]
          })
          return filteredRow
        })

        const csvRows = [
          headersToExport.join(','),
          ...rows.map((row) => headersToExport.map((h) => `"${String(row[h] ?? '').replace(/"/g, '""')}"`).join(',')),
        ]
        downloadCSV(csvRows.join('\n'), 'kit-request-history')
      })
    },
    [availableColumns, columns, omit, notify]
  )

  return (
    <Box>
      <List
        resource="provider-portal/kit-requests"
        filters={filters}
        actions={<ListActions exporter={kitRequestExporter} />}
        pagination={<Pagination rowsPerPageOptions={[50]} />}
        perPage={50}
        sort={{ field: 'createdAt', order: 'DESC' }}
        exporter={kitRequestExporter}
        disableSyncWithLocation
        sx={{
          '& .RaList-main': {
            marginTop: 0,
          },
          '& .MuiPaper-root': {
            boxShadow: 'none',
            border: '1px solid #e0e0e0',
          },
        }}
      >
        <DatagridConfigurable
          bulkActionButtons={false}
          omit={['providerGroupId', 'shippedDate', 'shippedInvoiceId', 'testedInvoiceId']}
          sx={{
            '& .MuiTableCell-head': {
              fontWeight: 700,
              backgroundColor: '#f5f5f5',
            },
          }}
        >
          <FunctionField
            source="requestId"
            label="Request ID"
            sortable={false}
            render={(record) => {
              const id = record.requestId
              return (
                <Tooltip title={record.requestId} placement="top">
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', cursor: 'default' }}>
                    {id && id.length > 6 ? `…` + id?.slice(-8) : id}
                  </Typography>
                </Tooltip>
              )
            }}
          />

          <ReferenceField source="providerGroupId" reference="provider-portal/provider-groups" label="Provider Group" link={false}>
            <TextField source="name" />
          </ReferenceField>

          <FunctionField source="clinicName" label="Clinic Name" render={(record) => record.clinicName || '—'} />

          <FunctionField source="providerName" label="Provider Name" render={(record) => record.providerName || '—'} />

          <FunctionField source="createdAt" label="Date / Time" render={(record) => fromTimestamp(record.createdAt) || '—'} />

          <FunctionField
            source="requestStatus"
            label="Status"
            render={(record) => (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <StatusChip value={record.requestStatus} config={REQUEST_STATUS_CONFIG} />
                <StatusChip value={record.shippedFinancialStatus} config={FINANCIAL_STATUS_CONFIG} />
              </Box>
            )}
          />

          <FunctionField
            source="patientName"
            label="Patient Name"
            render={(record) => [record.patientFirstName, record.patientLastName].filter(Boolean).join(' ') || '—'}
          />

          <FunctionField
            source="patientDob"
            label="Date of Birth"
            render={(record) => formatDateToMMDDYYYY(record.patientDob) || '—'}
          />

          <FunctionField
            source="dateOfVasectomy"
            label="Date of Vasectomy"
            render={(record) => formatDateToMMDDYYYY(record.dateOfVasectomy) || '—'}
          />

          <FunctionField source="shippedDate" label="Ship Date" render={(record) => formatDateToMMDDYYYY(record.shippedDate) || '—'} />

          <FunctionField
            source="shippedInvoiceId"
            label="Shipped Invoice ID"
            sortable={false}
            render={(record) => record.shippedInvoiceId || '—'}
          />

          <FunctionField
            source="testedInvoiceId"
            label="Tested Invoice ID"
            sortable={false}
            render={(record) => record.testedInvoiceId || '—'}
          />

          <FunctionField source="targetConvertDate" label="Target Convert Date" render={(record) => formatDateToMMDDYYYY(record.targetConvertDate) || '—'} />

          <FunctionField
            source="phoneNumber"
            label="Patient Contact"
            sortable={false}
            render={(record) => (
              <Box display="flex" flexDirection="column">
                <Typography variant="body2">{formatPhoneDisplay(record.phoneNumber) || '—'}</Typography>
                <Typography variant="body2">{record.email || '—'}</Typography>
              </Box>
            )}
          />

          <FunctionField label="Actions" render={(record) => <RowActions record={record} onEdit={onEdit} refresh={refresh} />} />
        </DatagridConfigurable>
      </List>
    </Box>
  )
}

export default KitRequestList
